/**
 * Shared utility functions for tables
 * Single source of truth for common table operations
 */

import { JOB_STATUSES, normalizeStatus } from '../../constants';

const JOB_STATUS_BY_VALUE = Object.fromEntries(JOB_STATUSES.map(s => [s.value, s]));

/**
 * Parse date strings safely (handles various formats)
 * 
 * Handles formats:
 * - 12/25/2024
 * - December 25, 2024
 * - Dec 25, 2024 3:00 PM
 * - Wednesday, December 25, 2024 3:00 PM MST
 * - 2024-12-25
 * - And many more...
 */
export function parseDateSafely(dateStr) {
  if (!dateStr || dateStr === 'N/A' || dateStr === 'TBD') return null;
  
  try {
    // Clean up common date string issues
    let cleanedDate = dateStr
      .replace(/(\d+)(st|nd|rd|th)/g, '$1') // Remove ordinal suffixes (13th -> 13)
      .replace(/\s+MST|\s+PST|\s+EST|\s+CST|\s+MDT|\s+PDT|\s+EDT|\s+CDT/gi, '') // Remove timezone abbreviations
      .replace(/^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday),?\s+/i, '') // Remove day of week at start
      .trim();
    
    const date = new Date(cleanedDate);
    // Check if date is valid
    if (isNaN(date.getTime())) return null;
    return date;
  } catch {
    return null;
  }
}

/**
 * Normalize date for display on list views
 * Converts messy scraped dates to clean, consistent format
 * 
 * Examples:
 * - "Wednesday, December 25, 2024 3:00 PM MST" → "Dec 25, 2024"
 * - "12/25/2024 3:00 PM" → "Dec 25, 2024"  
 * - "December 25, 2024" → "Dec 25, 2024"
 * 
 * @param {string} dateStr - Original scraped date string
 * @param {boolean} includeTime - Include time (for detail page)
 * @returns {string} Normalized date string or original if can't parse
 */
export function normalizeDate(dateStr, includeTime = false) {
  if (!dateStr || dateStr === 'N/A' || dateStr === 'TBD') return dateStr;
  
  const date = parseDateSafely(dateStr);
  if (!date) return dateStr; // Return original if can't parse
  
  // Format for list views: "Dec 25, 2024"
  if (!includeTime) {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
  
  // Format for detail page: "Dec 25, 2024 at 3:00 PM"
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Get due date style (color coding for urgency)
 * Returns inline style object
 */
export function getDueDateStyle(dueDateStr) {
  if (!dueDateStr || dueDateStr === 'N/A' || dueDateStr === 'TBD') {
    return { color: 'var(--text-muted)' };
  }
  
  try {
    const dueDate = parseDateSafely(dueDateStr);
    if (!dueDate) return {};
    
    const now = new Date();
    const diffTime = dueDate - now;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    
    if (diffDays < 0) {
      // Past due - BOLD RED
      return { 
        color: '#dc3545',  // Bootstrap red 
        fontWeight: 'bold',
        textDecoration: 'none'
      };
    } else if (diffDays <= 7) {
      // Within 1 week - BOLD DARK ORANGE
      return { 
        color: '#fd7e14',  // Bootstrap orange
        fontWeight: 'bold',
        textDecoration: 'none'
      };
    } else if (diffDays <= 14) {
      // Within 2 weeks - ORANGE
      return { 
        color: '#fd7e14',  // Bootstrap orange
        fontWeight: 'normal',
        textDecoration: 'none'
      };
    }
    
    return {}; // Normal
  } catch {
    return {};
  }
}

/**
 * Sort dates with proper handling of nulls and past dates
 * Sorts: Future dates (soonest first) → Past dates (most recent first) → Invalid/null dates (end)
 */
export function sortDates(dateA, dateB) {
  const parsedA = parseDateSafely(dateA);
  const parsedB = parseDateSafely(dateB);
  const now = new Date();
  
  // If both invalid, maintain order
  if (!parsedA && !parsedB) return 0;
  // Invalid dates go to end
  if (!parsedA) return 1;
  if (!parsedB) return -1;
  
  // Past dates go to end (but after future dates)
  const isPastA = parsedA < now;
  const isPastB = parsedB < now;
  
  if (isPastA && !isPastB) return 1;  // A past, B future → B first
  if (!isPastA && isPastB) return -1; // A future, B past → A first
  
  // Both past or both future, sort normally (earliest first)
  return parsedA - parsedB;
}

/**
 * Filter function for global search across all columns
 */
export function globalFilterFn(row, columnId, filterValue) {
  const searchLower = String(filterValue).toLowerCase();
  
  // Search across all string/number values in the row
  return Object.values(row.original).some(value => {
    if (value == null) return false;
    return String(value).toLowerCase().includes(searchLower);
  });
}

/**
 * Get status badge class name based on status (JOB_STATUSES is the single source of truth;
 * legacy user_status values are normalized first - see constants.js normalizeStatus).
 * 'completed' is a special legacy-only display state pending manual reassignment.
 */
export function getStatusBadgeClass(status) {
  if (status === 'completed') return 'badge bg-info';
  const normalized = normalizeStatus(status);
  return JOB_STATUS_BY_VALUE[normalized]?.badgeClass || 'badge bg-secondary';
}

/**
 * Get status badge display text (see getStatusBadgeClass)
 */
export function getStatusText(status) {
  if (status === 'completed') return 'Completed (legacy)';
  const normalized = normalizeStatus(status);
  return JOB_STATUS_BY_VALUE[normalized]?.label || status;
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text, maxLength = 50) {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Format date for display (consistent formatting)
 */
export function formatDate(dateStr) {
  const date = parseDateSafely(dateStr);
  if (!date) return dateStr || 'N/A';
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Format timestamp for display (with time)
 */
export function formatTimestamp(dateStr) {
  const date = parseDateSafely(dateStr);
  if (!date) return dateStr || 'N/A';
  
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Export data to CSV
 */
export function exportToCSV(data, filename = 'export.csv') {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }
  
  // Get all keys from first row
  const headers = Object.keys(data[0]);
  
  // Create CSV content
  const csvContent = [
    headers.join(','), // Header row
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Escape quotes and wrap in quotes if contains comma
        if (value == null) return '';
        const stringValue = String(value).replace(/"/g, '""');
        return stringValue.includes(',') ? `"${stringValue}"` : stringValue;
      }).join(',')
    )
  ].join('\n');
  
  // Download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

